import { formatMoney, titleCase, type BudgetCeiling, type DeviationWaitingDto } from '@nsb/core';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { api, errorText, queryClient } from '../../api.ts';
import { homeSection } from '../../components/homeWords.ts';
import { Button, ErrorLine } from '../../components/ui.tsx';

/**
 * Waiting on you, on a client's home: what NSB has set up for them and their own steps the work has reached, under its
 * heading. Its rows are the home's own (the budget to confirm here, a step of theirs `YoursRow`, a deviation to confirm
 * `DeviationWaitingRow`); the Introduction draws it with the same rows, fed its example.
 */
export function WaitingOnYou({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <section className={`self-start ${className}`}>
      <div className="border-b border-ink/40 pb-2">
        <h2 className="text-[1.375rem]">{homeSection('waiting')}</h2>
      </div>
      {children}
    </section>
  );
}

/**
 * NSB's request to lock the job's pattern language (spec "Pattern Mapping" §4.5): to the Patterns page, where they lock
 * it or answer Not yet with a note. Without `to` (NSB's preview of their home), the words alone.
 */
export function PatternsWaitingRow({ by, to }: { by: string | null; to?: string }) {
  const body = (
    <>
      <span>
        <span className="block group-hover:underline">Lock the pattern language</span>
        <span className="meta">Asked by {by ?? 'NSB'}: lock it, or answer Not yet with a note</span>
      </span>
      <span className="link shrink-0 text-sm text-ink-2">Open</span>
    </>
  );
  return (
    <li>
      {to ? (
        <Link to={to} className="group flex items-baseline justify-between gap-4 py-3.5">
          {body}
        </Link>
      ) : (
        <span className="flex items-baseline justify-between gap-4 py-3.5">{body}</span>
      )}
    </li>
  );
}

/**
 * A budget NSB proposed, waiting on the client's word, all in, with where its contingency sits: a link to their budget,
 * where they confirm it as proposed or with the contingency moved, or its words alone where it is drawn.
 */
export function BudgetWaitingRow({ split, by, to }: { split: BudgetCeiling; by: string; to?: string }) {
  const body = (
    <>
      <span>
        <span className="block group-hover:underline">
          Confirm your budget: <span className="num">{formatMoney(split.totalCents)}</span>
        </span>
        <span className="meta">
          Proposed by {by}, all in
          {split.contingencyCents <= 0 ? null : split.reserve === 'on_top' ? (
            <>
              : <span className="num">{formatMoney(split.workCents)}</span> for the work and the contingency on top
            </>
          ) : (
            ', the contingency inside it'
          )}
        </span>
      </span>
      <span className="link shrink-0 text-sm text-ink-2">Open</span>
    </>
  );
  return (
    <li>
      {to ? (
        <Link to={to} className="group flex items-baseline justify-between gap-4 py-3.5">
          {body}
        </Link>
      ) : (
        <span className="flex items-baseline justify-between gap-4 py-3.5">{body}</span>
      )}
    </li>
  );
}

/**
 * A deviation NSB proposed, waiting on the client (design study 21; Jason's answers, 2026-10-07): a step that comes late,
 * into the phase under way, or a gate marked done before the rest of its phase. In their words, with NSB's reason, and
 * Confirm or Decline with a word if they like (POST /process/deviations/:id/answer, on the record), as their own steps
 * are marked (`YoursRow`); answered, it drops off. Until they confirm, the step is held and the gate not done.
 */
export function DeviationWaitingRow({ projectId, deviation: d, preview }: { projectId: string; deviation: DeviationWaitingDto; preview: boolean }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState<'confirmed' | 'declined' | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const answer = async (a: 'confirmed' | 'declined') => {
    setBusy(a);
    setErr(null);
    try {
      await api(`/projects/${projectId}/process/deviations/${d.id}/answer`, { body: { answer: a, note: note.trim() || undefined } });
      await queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      setOpen(false);
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(null);
    }
  };
  const title = titleCase(d.title);
  return (
    <li className="py-3.5">
      <p className="flex items-baseline justify-between gap-4">
        <span>
          <span className="block">{d.kind === 'late' ? `${title} comes late, into ${d.landsIn ?? 'the phase under way'}` : `${title} to be marked done before the rest of ${d.phase}`}</span>
          <span className="meta">{d.kind === 'late' ? `${d.phase} is done · yours to confirm` : 'Yours to confirm'}</span>
        </span>
        {!open && !preview ? (
          <button type="button" className="link min-h-11 shrink-0 text-sm text-ink-2 hover:text-ink" onClick={() => setOpen(true)}>
            Confirm or decline
          </button>
        ) : null}
      </p>
      <p className="mt-1 max-w-measure text-[0.9375rem] text-ink-2">NSB’s reason: “{d.reason}”</p>
      {open ? (
        <div className="mt-2 rounded-[2px] bg-sheet p-3">
          <label className="block">
            <span className="meta">A word on it, if you like</span>
            <textarea className="field mt-1 min-h-16 w-full" value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} />
          </label>
          <div className="mt-2 flex flex-wrap gap-4">
            <Button tone="secondary" busy={busy === 'confirmed'} disabled={busy !== null} onClick={() => void answer('confirmed')}>
              Confirm
            </Button>
            <Button tone="secondary" busy={busy === 'declined'} disabled={busy !== null} onClick={() => void answer('declined')}>
              Decline
            </Button>
            <Button tone="quiet" onClick={() => setOpen(false)}>
              Not yet
            </Button>
          </div>
          <ErrorLine error={err} />
        </div>
      ) : null}
    </li>
  );
}
